import { useAtom } from 'jotai';
import { MouseEventHandler, useCallback, useEffect, useRef } from 'react';
import { imageDialogAtom } from '../atoms';
import { useOnClickOutside } from '../hooks';
import { cln } from '../lib/utils';
import { ARROW_LEFT_KEY, ARROW_RIGHT_KEY } from '../lib/constants';

const enum ArrowKeyDirections {
  back = 'back',
  forward = 'forward',
}

export function ImageDialog() {
  const [state, setState] = useAtom(imageDialogAtom);
  const dialog = useRef<HTMLDialogElement | null>(null);
  const forward = useRef<HTMLButtonElement | null>(null);
  const backward = useRef<HTMLButtonElement | null>(null);

  const handleClose = () => {
    setState({ isOpen: false, urls: [], index: 0 });
    dialog.current?.close();
  };

  const move = useCallback(
    (direction: ArrowKeyDirections) => {
      setState((prev) => {
        const newIndex =
          direction === 'back'
            ? Math.max(prev.index - 1, 0)
            : Math.min(prev.index + 1, prev.urls.length - 1);
        return { ...prev, index: newIndex };
      });
    },
    [setState],
  );

  const handleClickBackward: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();

    move(ArrowKeyDirections.back);
  };

  const handleClickForward: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();

    move(ArrowKeyDirections.forward);
  };

  useOnClickOutside(dialog.current, (e) => {
    if (e.target === forward.current || e.target === backward.current) return;
    handleClose();
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!state.isOpen) return;
      if (event.key === ARROW_LEFT_KEY) move(ArrowKeyDirections.back);
      if (event.key === ARROW_RIGHT_KEY) move(ArrowKeyDirections.forward);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [state.isOpen, move]);

  useEffect(() => {
    if (!dialog.current?.open && state.isOpen) dialog.current?.showModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  return (
    <dialog ref={dialog} className={cln('!p-0 w-[80%] h-[90%] place-items-center relative ')}>
      {state.index > 0 && (
        <button
          ref={backward}
          onClick={handleClickBackward}
          className="fixed text-hero top-1/2 left-4 -tanslate-y-1/2  h-20 w-20 cursor-pointer hover:bg-neutral-900 hover:bg-opacity-10 grid place-items-center rounded-full"
        >
          {'<'}
        </button>
      )}
      <img
        src={state.urls[state.index]}
        alt="report screenshot"
        className="block absolute max-h-full max-w-full"
      />
      {state.index < state.urls.length - 1 && (
        <button
          ref={forward}
          onClick={handleClickForward}
          className="fixed text-hero top-1/2 right-4 -tanslate-y-1/2 h-20 w-20 cursor-pointer hover:bg-neutral-900 hover:bg-opacity-10 grid place-items-center rounded-full "
        >
          {'>'}
        </button>
      )}
    </dialog>
  );
}
